import API_URLS from '../config/apiConfig';
import api from '../interceptors/axiosConfig';
import type {
  AuditActionResponse,
  AuditDetails,
  AuditUpdateRequest,
  AuditUpdateResponse,
  CreateManualAuditPayload,
  CreateManualAuditResponse,
  MostRecentAuditResponse,
  ReceiptAuditsParams,
  ReceiptAuditsResponse,
  ReceiptAuditNotesResponse,
  ReceiptAuditSimulationRequest,
  ReceiptAuditSimulationResponse,
  UploadReceiptOptions,
  UploadReceiptResponse,
  VendingInventoryPreviousDateResponse,
  VendingInventoryMappingContextResponse,
  VendingInventoryPackRecommendationRequest,
  VendingInventoryPackRecommendationResponse,
} from '../types/services/audit';
import { handleAuditError, handleUploadError } from '../utils/errorHandler';

export type { EnhancedTicketData, MostRecentAuditDetail, ReceiptAudit } from '../types/services/audit';

export type {
  AuditActionResponse,
  AuditDetails,
  AuditUpdateRequest,
  AuditUpdateResponse,
  CreateManualAuditPayload,
  CreateManualAuditResponse,
  MostRecentAuditResponse,
  ReceiptAuditsParams,
  ReceiptAuditsResponse,
  ReceiptAuditNotesResponse,
  ReceiptAuditSimulationRequest,
  ReceiptAuditSimulationResponse,
  UploadReceiptOptions,
  UploadReceiptResponse,
  VendingInventoryPreviousDateResponse,
  VendingInventoryMappingContextResponse,
  VendingInventoryPackRecommendationRequest,
  VendingInventoryPackRecommendationResponse,
} from '../types/services/audit';

class AuditService {
  // Thin helper to fetch a Blob from a URL (supports http(s) and blob: URLs)
  async getBlobFromUrl(url: string): Promise<Blob> {
    if (url.startsWith('blob:')) {
      const res = await fetch(url);
      return await res.blob();
    }
    const response = await api.get(url, { responseType: 'blob' });
    return response.data;
  }

  // Get receipt audits with optional filtering
  async getReceiptAudits(params: ReceiptAuditsParams = {}): Promise<ReceiptAuditsResponse> {
    const queryParams = new URLSearchParams();
    if (params.status) queryParams.append('status_filter', params.status);
    if (params.store_id) queryParams.append('store_id', params.store_id);
    if (params.report_type) queryParams.append('report_type', params.report_type);
    if (params.date_from) queryParams.append('date_from', params.date_from);
    if (params.date_to) queryParams.append('date_to', params.date_to);
    if (params.age_days !== undefined) queryParams.append('age_days', params.age_days.toString());
    if (params.shift_date_from) queryParams.append('shift_date_from', params.shift_date_from);
    if (params.shift_date_to) queryParams.append('shift_date_to', params.shift_date_to);
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.size) queryParams.append('size', params.size.toString());

    const url = `${API_URLS.AUDIT_RECEIPT_AUDITS}?${queryParams.toString()}`;
    const response = await api.get(url);
    return response.data;
  }

  // Upload receipt for audit
  async uploadReceipt(
    file: File,
    storeId: string,
    options?: UploadReceiptOptions
  ): Promise<UploadReceiptResponse> {
    if (!file) {
      throw new Error('No file provided');
    }

    if (!file.type.startsWith('image/')) {
      throw new Error('File must be an image');
    }

    if (file.size > 20 * 1024 * 1024) {
      throw new Error('File size must be less than 20MB');
    }

    const formData = new FormData();
    formData.append('file', file);

    const queryParams = new URLSearchParams();
    queryParams.append('store_id', storeId);
    if (options?.originalUserId) {
      queryParams.append('original_user_id', options.originalUserId);
    }
    if (options?.sourceQueueId) {
      queryParams.append('source_queue_id', options.sourceQueueId);
    }
    if (options?.storeLocationId) {
      queryParams.append('store_location_id', options.storeLocationId);
    }

    const url = `${API_URLS.AUDIT_UPLOAD_RECEIPT}?${queryParams.toString()}`;
    
    try {
      const response = await api.post(url, formData);

      return response.data;
    } catch (error: any) {
      const errorData = error.response?.data || {};
      const errorMessage = errorData.error || errorData.detail || `Upload failed: ${error.response?.statusText || 'Unknown error'}`;
      handleUploadError(error, 'receipt');
      throw new Error(errorMessage);
    }
  }

  // Get audit details
  async getAuditDetails(queueId: string): Promise<AuditDetails> {
    try {
      const url = API_URLS.AUDIT_GET_AUDIT_DETAILS(queueId);
      const response = await api.get(url);
      return response.data;
    } catch (error: any) {
      console.error('Error fetching audit details:', error);
      handleAuditError(error, 'fetch');
      throw error;
    }
  }

  async getReceiptAuditNotes(queueId: string): Promise<ReceiptAuditNotesResponse> {
    const url = API_URLS.AUDIT_RECEIPT_AUDIT_NOTES(queueId);
    const response = await api.get(url);
    return response.data;
  }

  async addReceiptAuditNote(queueId: string, note: string): Promise<ReceiptAuditNotesResponse> {
    const url = API_URLS.AUDIT_RECEIPT_AUDIT_NOTES(queueId);
    const response = await api.post(url, { note });
    return response.data;
  }

  // Get audit image
  async getAuditImage(queueId: string): Promise<Blob> {
    try {
      const url = API_URLS.AUDIT_GET_AUDIT_IMAGE(queueId);
      const response = await api.get(url, { responseType: 'blob' });

      return response.data;
    } catch (error: any) {
      console.error('Error fetching audit image:', error);
      handleAuditError(error, 'fetch');
      const status = error?.response?.status;
      const statusText = error?.response?.statusText;
      const baseMessage = error?.message || 'Unknown error';
      const statusLabel = status ? `${status}${statusText ? ` ${statusText}` : ''}` : '';
      const details = statusLabel ? `${statusLabel} (${baseMessage})` : baseMessage;
      throw new Error(`Failed to fetch image: ${details}`);
    }
  }

  // Update receipt image by queue ID
  async updateReceiptImage(queueId: string, file: File): Promise<UploadReceiptResponse> {
    if (!file) {
      throw new Error('No file provided');
    }

    if (!file.type.startsWith('image/')) {
      throw new Error('File must be an image');
    }

    if (file.size > 20 * 1024 * 1024) {
      throw new Error('File size must be less than 20MB');
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('queue_id', queueId);

    const url = API_URLS.AUDIT_UPDATE_RECEIPT(queueId);
    
    try {
      const response = await api.post(url, formData);

      return response.data;
    } catch (error: any) {
      const errorData = error.response?.data || {};
      throw new Error(errorData.error || errorData.detail || `Update failed: ${error.response?.statusText || 'Unknown error'}`);
    }
  }

  // Update audit status
  async updateAuditStatus(queueId: string, request: AuditUpdateRequest): Promise<AuditUpdateResponse> {
    const url = API_URLS.AUDIT_UPDATE_AUDIT_STATUS(queueId);
    const response = await api.post(url, request);
    return response.data;
  }

  // Update receipt audit status with report-type awareness.
  async updateReceiptAuditStatusByReportType(
    queueId: string,
    reportType: string,
    request: AuditUpdateRequest,
    storeId?: string,
  ): Promise<AuditUpdateResponse> {
    if (request.action === 'reject') {
      return this.updateAuditStatus(queueId, request);
    }

    if (reportType === 'vending_sales') {
      const url = API_URLS.AUDIT_UPDATE_VENDING_SALES_STATUS(queueId);
      const response = await api.post(url, request);
      return response.data;
    }

    if (reportType === 'vending_inventory') {
      const url = API_URLS.AUDIT_UPDATE_VENDING_INVENTORY_STATUS(queueId);
      const response = await api.post(url, request);
      return response.data;
    }

    if (reportType === 'vending_shift_report') {
      const url = API_URLS.AUDIT_UPDATE_VENDING_SHIFT_REPORT_STATUS(queueId);
      const response = await api.post(url, request);
      return response.data;
    }

    return this.updateAuditStatus(queueId, request);
  }

  // Simulate receipt audit (pre-submit preview)
  async simulateReceiptAudit(
    queueId: string,
    request: ReceiptAuditSimulationRequest = {}
  ): Promise<ReceiptAuditSimulationResponse> {
    const url = API_URLS.AUDIT_SIMULATE_RECEIPT(queueId);
    const response = await api.post(url, request);
    return response.data;
  }

  // Revert audit to ready status
  async revertToReady(queueId: string): Promise<AuditActionResponse> {
    const url = API_URLS.AUDIT_REVERT_TO_READY(queueId);
    const response = await api.post(url, {});
    return response.data;
  }

  // Reprocess audit
  async reprocessAudit(queueId: string): Promise<AuditActionResponse> {
    const url = API_URLS.AUDIT_REPROCESS(queueId);
    const response = await api.post(url, {});
    return response.data;
  }

  // Get most recent audit details for comparison
  async getMostRecentAuditDetails(storeId: string, beforeDatetime?: string, timezone: string = 'UTC'): Promise<MostRecentAuditResponse> {
    const queryParams = new URLSearchParams();
    if (beforeDatetime) queryParams.append('before_datetime', beforeDatetime);
    queryParams.append('timezone', timezone);
    
    const url = `${API_URLS.AUDIT_GET_MOST_RECENT_DETAILS(storeId)}?${queryParams.toString()}`;
    
    const response = await api.get(url);
    return response.data;
  }

  async getVendingInventoryPreviousDate(queueId: string): Promise<VendingInventoryPreviousDateResponse> {
    const url = API_URLS.AUDIT_GET_VENDING_INVENTORY_PREVIOUS_DATE(queueId);
    const response = await api.get(url);
    return response.data;
  }

  async getVendingInventoryMappingContext(queueId: string): Promise<VendingInventoryMappingContextResponse> {
    const url = API_URLS.AUDIT_GET_VENDING_INVENTORY_MAPPING_CONTEXT(queueId);
    const response = await api.get(url);
    return response.data;
  }

  async getVendingInventoryPackRecommendations(
    queueId: string,
    request: VendingInventoryPackRecommendationRequest,
  ): Promise<VendingInventoryPackRecommendationResponse> {
    const url = API_URLS.AUDIT_GET_VENDING_INVENTORY_PACK_RECOMMENDATIONS(queueId);
    const response = await api.post(url, request);
    return response.data;
  }

  // Create manual audit with ticket details
  async createManualAudit(storeId: string, payload: CreateManualAuditPayload): Promise<CreateManualAuditResponse> {
    const url = API_URLS.AUDIT_CREATE_MANUAL_AUDIT(storeId);
    const response = await api.post(url, payload);
    return response.data;
  }
}

// Export singleton instance
const auditService = new AuditService();
export default auditService; 
